import isEqual from 'lodash.isequal';
import { type FC, type PropsWithChildren, useCallback, useEffect, useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { AttachmentsContextProvider } from '../context/attachments-context';
import { useAttachmentsReducer } from '../hooks/use-attachments-reducer';

interface AttachmentsRootProps extends PropsWithChildren {
  value?: any[];
  defaultValue?: any[];
  onChange?: (value: any[], isFullInputChange: boolean) => void;
  onLoadingChange?: (isLoading: boolean) => void;
}

export const AttachmentsRoot: FC<AttachmentsRootProps> = ({
  children,
  value,
  defaultValue = [],
  onChange,
  onLoadingChange,
}) => {
  const [state, dispatch] = useAttachmentsReducer(defaultValue);

  const removeAttachment = useCallback((fileKey: string) => {
    dispatch({
      type: 'REMOVE_ATTACHMENT',
      fileKey,
    });
  }, []);

  const setFileToUpload = useCallback((file: File) => {
    const fileKey = `${uuid()}.${file.name}`;
    const abortController = new AbortController();

    dispatch({
      type: 'SET_FILE_TO_UPLOAD',
      file,
      fileKey,
      abortController,
    });

    dispatch({
      type: 'SET_UPLOAD_COMPLETED',
      fileKey,
    });
  }, []);

  const handleAttachmentsRecordChange = () => {
    const result: any[] = [];
    let isLoading = false;

    state.attachmentsRecord.forEach((attachment) => {
      result.push({
        fileKey: attachment.fileKey,
        fileName: attachment.fileName,
        size: attachment.size,
        mimeType: attachment.mimeType,
      });

      if (attachment.state === 'uploading') {
        isLoading = true;
      }
    });

    onLoadingChange?.(isLoading);

    // TODO: возникает ошибка: attachments-root.tsx:93 Warning: Maximum update depth exceeded. This can happen when a component calls setState inside useEffect, but useEffect either doesn't have a dependency array, or one of the dependencies changes on every render. - причина бага с "дерганием экрана" при загрузке файлов в чат, если прокинуть в value значние из стейта а через onChange прокидывать state
    onChange?.(result, true);
  };

  useEffect(() => {
    handleAttachmentsRecordChange();
  }, [state.attachmentsRecord]);

  useEffect(() => {
    const currentFiles: string[] = [];
    state.attachmentsRecord.forEach((attachment) => {
      currentFiles.push(attachment.fileKey);
    });

    const valueFileKeys = value?.map((item) => item.fileKey) ?? [];

    if (!isEqual(currentFiles, valueFileKeys)) {
      dispatch({
        type: 'RESET_STATE',
        files: value || [],
      });
    }
  }, [value]);

  const contextProviderValue = useMemo(
    () => ({
      fileKeys: state.fileKeys,
      attachmentsRecord: state.attachmentsRecord,
      setFileToUpload,
      removeAttachment,
    }),
    [state, setFileToUpload, removeAttachment],
  );

  return (
    <AttachmentsContextProvider value={contextProviderValue}>{children}</AttachmentsContextProvider>
  );
};
